import * as CL from '@design-system/component-library';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { generatePath, useLoaderData } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables.js';
import { monthMsg, omaElisaForEmployeeMsg, shopMsg, t } from '../../common/i18n/index.js';
import { getPublishedCatalogs, getSelectedCatalog, toDisplayableProduct } from '../../common/utils/catalogUtils.js';
import { categories } from '../../common/utils/categoryUtils.js';
import { deepEqual } from '../../common/utils/objectUtils.js';
import { getVatAsString } from '../../common/utils/taxUtils.js';
import { Catalog, OnlineModelCategory } from '../../generated/api/models.js';
import { useOnlineModelsContext } from '../../public/site/onlineModel/onlineModelContextProvider.js';
import { useRedirectToDeviceStore } from '../../public/site/path/Employee/DeviceStore/EoeStore.js';
import { Grid } from '../Grid/Grid';
import { HeroHeading, HeroHeadingType } from '../HeroHeading/index.js';
import { Loading } from '../Loading/index.js';
import { NavigationMenu, NavigationMenuItem } from '../NavigationMenu/NavigationMenu.js';
import { ProductGrid } from '../ProductGrid/ProductGrid.js';
import type { Category } from '../../common/types/category.js';
import type { OnlineModelHeader } from '../../generated/api/onlineModelHeader.js';
import type { State } from '../../selfservice/common/store.js';
import type { ProductGridItem } from '../ProductGrid/ProductGrid.js';

import './EoeDeviceStoreProducts.scss';

interface EoeDeviceStoreProductsProps {
  category: Category;
}

const getCategoryLinks = (productCategories: OnlineModelCategory[], selectedCategory: string) =>
  Object.values(categories)
    .filter(category => productCategories.includes(category.code))
    .sort((c1, c2) => c1.order - c2.order)
    .map(({ displayText, iconClass, urlPath, ...category }) => (
      <NavigationMenuItem
        isActive={selectedCategory === category.code}
        key={urlPath}
        id={urlPath}
        to={generatePath(paths.EMPLOYEE_DEVICE_STORE_PRODUCTS, { category: urlPath })}
        icon={<CL.Icon icon={`${iconClass === 'phone' ? 'smart' : ''}${iconClass}`} size="l" />}
        label={displayText()}
      />
    ));

const getHeroSubtitle = (catalog: Catalog, category: Category) => {
  if (category.code === OnlineModelCategory.ACCESSORIES || catalog.productType === Catalog.ProductTypeEnum.ONETIME) {
    return undefined;
  }
  let subtitle = `${t.V3UP('Device contract period')} ${catalog.contractPeriod} ${t.XXVX(monthMsg)}.`;
  if (catalog.productType === Catalog.ProductTypeEnum.EPP_RECURRING) {
    subtitle = subtitle.concat(` ${t.KD2N('Quoted prices VAT')} ` + getVatAsString() + '%');
  }
  return subtitle;
};

export const EoeDeviceStoreProducts = ({ category }: EoeDeviceStoreProductsProps) => {
  useRedirectToDeviceStore();
  const { selectedCatalogCode, virtualCatalogItems } = useSelector(
    (state: State) => ({
      selectedCatalogCode: state.selfservice?.virtualCatalogs?.selectedCatalogCode,
      virtualCatalogItems: state.selfservice?.virtualCatalogs?.items,
    }),
    deepEqual
  );
  const headers = useLoaderData() as OnlineModelHeader[];
  const { fetchHeadersByCodes, priceAlteredModels, isHeaderCodeResolved } = useOnlineModelsContext();
  const catalogs: Catalog[] = getPublishedCatalogs(virtualCatalogItems) || [];
  const catalog = getSelectedCatalog(catalogs, selectedCatalogCode);
  const models = headers;
  const gridItems: ProductGridItem[] =
    (catalog &&
      models
        ?.map(model => priceAlteredModels?.onlineModelHeadersByCode?.[model.onlineModelCode] ?? model)
        .map(model => toDisplayableProduct(model, catalog, true, true))
        .filter((product): product is ProductGridItem => Boolean(product))
        .map(product => ({
          ...product,
          isPriceLoading: !isHeaderCodeResolved(product.code),
          pagePath: generatePath(paths.EMPLOYEE_DEVICE_STORE_PRODUCT, {
            category: category.urlPath,
            deviceName: product?.pagePath?.substring(1) || '',
          }),
        }))) ??
    [];

  useEffect(() => {
    // Only fetch private (priced) headers for models actually in the selected catalog —
    // the grid renders just those (toDisplayableProduct filters by catalog.products), so
    // fetching the whole category would waste costly private header requests.
    const catalogProductCodes = new Set(catalog?.products ?? []);
    const codesToFetch = (models ?? [])
      .map(model => model.onlineModelCode)
      .filter(code => catalogProductCodes.has(code));
    if (codesToFetch.length) {
      fetchHeadersByCodes(codesToFetch);
    }
  }, [fetchHeadersByCodes, models, catalog]);

  if (!selectedCatalogCode || !catalog) {
    return null;
  }

  return (
    <div className="of-employee-device-store-products">
      <HeroHeading
        center={true}
        fullScreen={true}
        breadCrumbPaths={[
          { name: t.H6JV(omaElisaForEmployeeMsg), path: paths.EMPLOYEE_HOME },
          { name: t.BZGP(shopMsg), path: paths.EMPLOYEE_DEVICE_STORE },
          { name: catalog.name },
        ]}
        heroHeadingType={HeroHeadingType.BLANK}
        title={catalog.name}
        subtitle={getHeroSubtitle(catalog, category)}
      />
      {catalog.productCategories.length > 1 && (
        <Grid colWidthL={10} className={`${dsClass.MARGIN_BOTTOM_4} ${dsClass.MARGIN_LEFT_5}`}>
          <div className="of-catalog-products-selection__border-bottom">
            <NavigationMenu>{getCategoryLinks(catalog.productCategories, category.code)}</NavigationMenu>
          </div>
        </Grid>
      )}
      {models ? (
        <ProductGrid items={gridItems} category={category.code} itemLimit={48} inEOE={true} />
      ) : (
        <Loading big={true} />
      )}
    </div>
  );
};
